import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { landingRedirect } from '../utils/workspace';

/*
 * The signed-in gate around every page of the app (App.js).
 *
 * Nobody signed in goes to /login with `from`, the whole location -- path,
 * query and hash -- so Login sends them back exactly where they were going
 * (pages/Login.js `enter`): a link in an email included. The assignment
 * email's /field?sheet=tasks&mission=<id> (plan phase 6) opens Field Mode's
 * Tasks sheet on that card after the sign-in, and landingRedirect, which
 * acts only on a bare '/', never sends it to the workspace chooser instead.
 */
export default function PrivateRoute({ children, allowGuest = false }) {
  const { user } = useAuth();
  const location = useLocation();
  // `from` lets Login send the person back where they were going (a link in a
  // notification, a bookmark) instead of to the home page.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (user.must_change_password) return <Navigate to="/change-password" />;
  // 'public'-role users have no business in the staff app shell; their entire
  // surface is the standalone /survey page (a separate top-level route, so
  // this can't loop back through PrivateRoute).
  // The one exception: Field Mode for a live field event the account was let
  // into as a guest (`allowGuest`; pages/Field.js sends it back to /survey
  // anywhere else).
  if (user.roles?.includes('public')) return allowGuest ? children : <Navigate to="/survey" />;
  // Workspace choice comes AFTER both hard redirects above: someone who must
  // change their password should not be asked which bench they are at first,
  // and a 'public' account is never asked at all. landingRedirect only fires
  // on a bare '/' with no stored choice, so a deep link into /plates (from a
  // code lookup, a hub row) still lands on /plates.
  const landing = landingRedirect(user, location);
  if (landing) return <Navigate to={landing} replace />;
  return children;
}
